/**
 * CategoryManager Component
 * Super-admin UI for managing session categories (add / edit / delete).
 */
import React, { useState, useEffect } from 'react';
import AdminApiService from '../services/AdminApiService';
import { invalidateCategoryCache } from '../hooks/useCategories';
import './CategoryManager.css';

// ── Small inline form ────────────────────────────────────────────────────────
const CategoryForm = ({ initial, onSave, onCancel, isEdit }) => {
  const [code, setCode]        = useState(initial?.code        || '');
  const [name, setName]        = useState(initial?.displayName || '');
  const [color, setColor]      = useState(initial?.color       || '#005f99');
  const [saving, setSaving]    = useState(false);
  const [err, setErr]          = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || (!isEdit && !code.trim())) {
      setErr('Code and name are required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (isEdit) {
        await AdminApiService.updateCategory(initial.code, { displayName: name, color });
      } else {
        await AdminApiService.createCategory(code, name, color);
      }
      onSave();
    } catch (e) {
      setErr(e.message || 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cm-form" onSubmit={handleSubmit}>
      {!isEdit && (
        <div className="cm-field">
          <label>Code</label>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
            placeholder="e.g. GENAI"
            maxLength={50}
            required
          />
          <span className="cm-hint">Uppercase, underscores only. Cannot be changed later.</span>
        </div>
      )}
      <div className="cm-field">
        <label>Display Name</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. GenAI"
          maxLength={100}
          required
        />
      </div>
      <div className="cm-field cm-field--color">
        <label>Color</label>
        <div className="cm-color-row">
          <input type="color" value={color} onChange={e => setColor(e.target.value)} />
          <span className="cm-color-hex">{color}</span>
          <span className="cm-color-preview" style={{ background: color }} />
        </div>
      </div>
      {err && <p className="cm-err">{err}</p>}
      <div className="cm-form-actions">
        <button type="submit" className="cm-btn cm-btn--primary" disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Category')}
        </button>
        <button type="button" className="cm-btn cm-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const CategoryManager = () => {
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [showAddForm, setShowAddForm]   = useState(false);
  const [editingCode, setEditingCode]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { code, name }
  const [deleteErr, setDeleteErr]       = useState('');     // in-use message

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await AdminApiService.getCategories();
      setCategories(res.data.data || res.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const afterSave = () => {
    invalidateCategoryCache();
    setShowAddForm(false);
    setEditingCode(null);
    load();
  };

  const requestDelete = (cat) => {
    setDeleteErr('');
    setDeleteConfirm(cat);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteErr('');
    try {
      await AdminApiService.deleteCategory(deleteConfirm.code);
      invalidateCategoryCache();
      setDeleteConfirm(null);
      load();
    } catch (e) {
      // e.message may include session count from backend
      setDeleteErr(e.message || 'Cannot delete category.');
    }
  };

  if (loading) return <div className="cm-loading">Loading categories…</div>;
  if (error)   return <div className="cm-error">❌ {error}</div>;

  return (
    <div className="cm-root">
      <div className="cm-header">
        <h2>🏷️ Category Management</h2>
        {!showAddForm && (
          <button className="cm-btn cm-btn--primary" onClick={() => { setShowAddForm(true); setEditingCode(null); }}>
            + Add Category
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="cm-card cm-card--form">
          <h3>New Category</h3>
          <CategoryForm isEdit={false} onSave={afterSave} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="cm-list">
        {categories.length === 0 && (
          <p className="cm-empty">No categories yet. Add one above.</p>
        )}
        {categories.map(cat => (
          <div key={cat.code} className="cm-item">
            {editingCode === cat.code ? (
              <div className="cm-item-edit">
                <CategoryForm
                  isEdit
                  initial={cat}
                  onSave={afterSave}
                  onCancel={() => setEditingCode(null)}
                />
              </div>
            ) : (
              <div className="cm-item-view">
                <div className="cm-item-left">
                  <span className="cm-swatch" style={{ background: cat.color || '#005f99' }} />
                  <div className="cm-item-info">
                    <span className="cm-item-name">{cat.displayName}</span>
                    <span className="cm-item-code">{cat.code}</span>
                  </div>
                </div>
                <div className="cm-item-actions">
                  <button
                    className="cm-btn cm-btn--sm"
                    onClick={() => { setEditingCode(cat.code); setShowAddForm(false); }}
                  >
                    Edit
                  </button>
                  <button
                    className="cm-btn cm-btn--sm cm-btn--danger"
                    onClick={() => requestDelete(cat)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="cm-overlay">
          <div className="cm-dialog">
            <h3>Delete Category</h3>
            <p>
              Delete <strong>{deleteConfirm.displayName}</strong> (<code>{deleteConfirm.code}</code>)?
            </p>
            <p className="cm-dialog-warn">This cannot be undone.</p>
            {deleteErr && <p className="cm-err">{deleteErr}</p>}
            <div className="cm-form-actions">
              {!deleteErr && (
                <button className="cm-btn cm-btn--danger" onClick={confirmDelete}>
                  Yes, Delete
                </button>
              )}
              <button
                className="cm-btn cm-btn--ghost"
                onClick={() => { setDeleteConfirm(null); setDeleteErr(''); }}
              >
                {deleteErr ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
